import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function MegaMenu({
	setShowDropdown,
	categories,
	handleMouseEnter,
	handleMouseLeave,
}) {
	const [activeTab, setActiveTab] = useState(null);
	const [activeCategory, setActiveCategory] = useState(null);

	useEffect(() => {
		if (categories?.length > 0) {
			const firstCategory = categories[0];
			setActiveTab(firstCategory.id);
			setActiveCategory(firstCategory);
		}
	}, [categories]);

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, ease: "easeInOut" }}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				className="absolute top-[105%] left-1/2 -translate-x-1/2 w-full max-w-[75%] lg:max-w-[80%] 2xl:lg:max-w-[60%] bg-white/95 backdrop-blur-md rounded-lg z-1000 shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-100 overflow-hidden"
			>
				<div className="flex">
					{/* --- Left Sidebar: Categories --- */}
					<div className="w-1/4 min-w-60 bg-white p-3 pr-0 flex flex-col gap-1">
						<p className="text-xs font-bold text-brand-500 uppercase tracking-widest mb-3 px-4">
							Categories
						</p>
						{categories?.map((tab, index) => (
							<button
								key={tab.id}
								onMouseEnter={() => {
									setActiveTab(tab.id);
									setActiveCategory(tab);
								}}
								className={`group relative flex items-center gap-3 px-4 py-3 text-left  rounded-l-lg
                                ${activeTab === tab.id ? "bg-[#e9e9e9] text-brand-500 font-semibold" : "text-gray-600 ha:bg-gray-50 ha:text-brand-500"}
                                `}
							>
								{/* Icon with Dynamic Filter */}
								<img
									src={tab.icon_url}
									alt={tab.title}
									className={`w-10 h-10 object-contain
                                    ${activeTab === tab.id ? "filter-[brightness(0)_saturate(100%)_invert(58%)_sepia(81%)_saturate(2157%)_hue-rotate(158deg)_brightness(96%)_contrast(93%)]" : "opacity-70 gh:opacity-100"}
                                    `}
								/>
								<span className="text-md">{tab.title}</span>

								{/* Custom "Negative Radius" Curves for Active Tab */}
								{activeTab === tab.id && (
									<>
										{/* Top Curve */}
										<div className="absolute -top-4 right-0 w-4 h-4 bg-[#e9e9e9] mask-[radial-gradient(circle_at_0_0,transparent_70%,black_70%)]" />
										{/* Bottom Curve */}
										<div className="absolute -bottom-4 right-0 w-4 h-4 bg-[#e9e9e9] mask-[radial-gradient(circle_at_0_100%,transparent_70%,black_70%)]" />
										{/* Indicator Line */}
										<div className="absolute left-0 top-1/4 bottom-1/4 w-1 bg-brand-500 rounded-full" />
									</>
								)}
							</button>
						))}
					</div>

					{/* --- Right Content: Items Grid --- */}
					<div className="flex-1 bg-[#e9e9e9] p-6 relative min-h-138.25">
						<motion.div
							key={activeTab}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.2, ease: "easeInOut" }}
							className="h-full flex flex-col"
						>
							{/* Grid Header */}
							<div className="mb-6">
								<h3 className="text-xl font-bold text-gray-800">
									{activeCategory?.title}
								</h3>
								<div className="h-1 w-12 bg-[#2abaf7] mt-1 rounded-lg" />
							</div>

							{/* Items Grid */}
							<div className="grid grid-cols-2 lg:grid-cols-3 gap-6 flex-1">
								{activeCategory?.service_items?.slice(0, 6).map((item, idx) => (
									<Link
										key={idx}
										to={`/services/${activeCategory.slug}/${item.slug}`}
										onClick={() => setShowDropdown(false)}
										className="group flex flex-col max-h-42.5 bg-white rounded-lg overflow-hidden shadow-sm ha:shadow-xl transition-all duration-150 transform ha:-translate-y-1"
									>
										<div className="relative overflow-hidden aspect-6/3">
											<img
												src={item.image}
												alt={item.title}
												className="w-full h-full object-cover transition-transform duration-150 gh:scale-110"
											/>
											<div className="absolute inset-0 bg-black/5 gh:bg-transparent transition-colors" />
										</div>
										<div className="p-3 text-center">
											<p className="text-sm font-semibold text-gray-700 gh:text-[#2abaf7] transition-colors line-clamp-1">
												{item.title}
											</p>
										</div>
									</Link>
								))}
							</div>

							{/* Footer CTA */}
							<div className="mt-6 pt-4 border-t border-gray-200/50 flex justify-end">
								<Link
									to={`/services/${activeCategory?.slug}`}
									onClick={() => setShowDropdown(false)}
									className="group flex items-center gap-2 text-sm font-bold text-brand-500 bg-white px-5 py-2.5 rounded-lg shadow-sm ha:shadow-md transition-all active:scale-95"
								>
									Explore All {activeCategory?.title}
									<span className="transition-transform gh:translate-x-1">
										&rarr;
									</span>
								</Link>
							</div>
						</motion.div>
					</div>
				</div>
			</motion.div>
		</AnimatePresence>
	);
}
